import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router'
import { useChannel } from '@/context/ChannelContext'
import {
  getPostMetrics,
  getShowcasePosts,
  getShowcasePreview,
  PostMetrics,
  PreviewResponse,
  ShowcaseApiError,
  ShowcasePost,
  VisitsBucket,
} from '@/api/showcase'

/**
 * A5 — Карточка поста.
 *
 * Источники данных:
 *  1) location.state.post — мгновенный рендер при переходе из A4 (без HTTP).
 *  2) B6 /posts list + find by postId — fallback при прямом переходе по URL.
 *  3) B18 /preview-html — отдельный запрос для browser-frame справа.
 *
 * Метрики берём из B6 (visitsTotal, qualityScore) — per-period (B7) добавим
 * следующим проходом.
 */
export function PostCardPage() {
  const { postId } = useParams<{ postId: string }>()
  const { selectedChannel } = useChannel()
  const location = useLocation() as { state?: { post?: ShowcasePost } }
  const navigate = useNavigate()

  const stateFromList = location.state?.post
  const [post, setPost] = useState<ShowcasePost | null>(
    stateFromList && stateFromList.postId === postId ? stateFromList : null,
  )
  const [postError, setPostError] = useState<string | null>(null)
  const [postLoading, setPostLoading] = useState(!post)

  const [preview, setPreview] = useState<PreviewResponse | null>(null)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [previewLoading, setPreviewLoading] = useState(true)

  const [metrics, setMetrics] = useState<PostMetrics | null>(null)
  const [metricsLoading, setMetricsLoading] = useState(true)
  const [metricsRange, setMetricsRange] = useState<30 | 90>(30)

  // Fallback fetch — когда зашли по прямому URL без state.
  useEffect(() => {
    if (post) return
    if (!postId || !selectedChannel?.uuid) return
    setPostLoading(true)
    setPostError(null)
    // B7 ещё нет — пока ищем в общем листе. limit 200 для безопасности.
    getShowcasePosts(selectedChannel.uuid, { limit: 200 })
      .then((res) => {
        const found = res.items.find((p) => p.postId === postId)
        if (found) setPost(found)
        else setPostError('Пост не найден в этом канале')
      })
      .catch((e) => {
        setPostError(e instanceof Error ? e.message : 'Не удалось загрузить пост')
      })
      .finally(() => setPostLoading(false))
  }, [post, postId, selectedChannel?.uuid])

  // B7 per-post metrics — параллельно с post + preview.
  // Только для опубликованных/скрытых — для queued/processing/failed данных нет.
  useEffect(() => {
    if (!postId || !selectedChannel?.uuid) return
    // Если уже знаем статус и метрик не будет — пропускаем (но не блокируем
    // первый рендер, когда post ещё null).
    if (post && (post.status === 'queued' || post.status === 'processing' || post.status === 'failed' || post.status === 'quality_rejected')) {
      setMetricsLoading(false)
      setMetrics(null)
      return
    }
    setMetricsLoading(true)
    getPostMetrics(selectedChannel.uuid, postId, metricsRange)
      .then((res) => setMetrics(res))
      .catch(() => setMetrics(null))
      .finally(() => setMetricsLoading(false))
  }, [postId, selectedChannel?.uuid, metricsRange, post?.status])

  // Preview HTML — параллельно с основной загрузкой.
  // Для status='deindexed' / 'quality_rejected' / 'failed' preview-файла на
  // портале нет (см. диагноз BUG-1 от B 15.06.2026): B6 list отдаёт postId,
  // но portal вернёт 404 потому что файла нет. Скипаем сразу — UX чище,
  // меньше шума в логах.
  const noPreviewStatuses: Array<ShowcasePost['status']> = ['deindexed', 'quality_rejected', 'failed']
  const skipPreview = post ? noPreviewStatuses.includes(post.status) : false
  // Для published — страница уже public, рендерим её напрямую через src.
  // Это избегает srcDoc-проблем (относительные пути в sandbox-контексте) и
  // даёт настоящий рендер (CSS+JS+изображения). B18 preview-html оставляем
  // только как fallback для статусов без публичной страницы.
  const isPublished = post?.status === 'published' && !!post?.url
  const skipFetch = skipPreview || isPublished

  useEffect(() => {
    if (!postId || !selectedChannel?.uuid) return
    if (skipFetch) {
      setPreviewLoading(false)
      setPreview(null)
      setPreviewError(null)
      return
    }
    setPreviewLoading(true)
    setPreviewError(null)
    getShowcasePreview(postId, selectedChannel.uuid)
      .then((res) => setPreview(res))
      .catch((e) => {
        // B18 коды от B (15.06): 410 POST_DEINDEXED / 404 NOT_PUBLISHED /
        // 404 POST_NOT_FOUND. Каждый — отдельный UX, не общий «не удалось».
        if (e instanceof ShowcaseApiError) {
          setPreviewError(e.code || `HTTP_${e.status}`)
        } else {
          setPreviewError('UNKNOWN')
        }
      })
      .finally(() => setPreviewLoading(false))
  }, [postId, selectedChannel?.uuid, skipFetch])

  const previewCanonical = useMemo(
    () => previewUrlFromHtml(preview?.html) || post?.url || 'telegraphyx.ru/channels/p/…',
    [preview?.html, post?.url],
  )

  return (
    <div className="card-page">
      <button className="back-btn" onClick={() => navigate('/showcase/posts')}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 12H5M11 6l-6 6 6 6" />
        </svg>
        Назад к&nbsp;списку
      </button>

      <div className="card-content">
        <div className="left-col">
          {postLoading ? (
            <div className="post-skeleton">
              <div className="sk-line w70" />
              <div className="sk-line w90" />
              <div className="sk-line w60" />
            </div>
          ) : postError ? (
            <div className="post-error">{postError}</div>
          ) : post ? (
            <>
              <PostSummary post={post} />
              <MetricsSection
                metrics={metrics}
                loading={metricsLoading}
                range={metricsRange}
                onRangeChange={setMetricsRange}
                postStatus={post.status}
              />
            </>
          ) : null}
        </div>

        <div className="right-col">
          <div className="preview-eyebrow">Превью страницы на портале</div>
          {skipPreview && post ? (
            <NoPreviewNotice status={post.status} />
          ) : isPublished && post ? (
            <BrowserFrameLive url={post.url} />
          ) : (
            <>
              <BrowserFrame
                loading={previewLoading}
                error={previewError}
                preview={preview}
                url={previewCanonical}
              />
              {preview?.meta && (
                <div className="preview-meta">
                  <MetaItem label="Статус" value={statusLabel(preview.meta.status)} />
                  {preview.meta.qualityScore !== null && (
                    <MetaItem label="Quality" value={String(preview.meta.qualityScore)} />
                  )}
                  {preview.meta.noindex && (
                    <MetaItem label="Индекс" value="не индексируется" warn />
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <style>{`
        .card-page { display: flex; flex-direction: column; gap: 18px; }
        .back-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          align-self: flex-start;
          padding: 8px 14px;
          background: var(--tg-surface, #fff);
          border: 1px solid var(--tg-line-2, #f0f0f4);
          border-radius: 8px;
          color: var(--tg-ink-2, #333);
          font-family: inherit;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
        }
        .back-btn:hover { background: var(--tg-paper, #fafafd); color: var(--tg-ink, #111); }
        .back-btn svg { width: 14px; height: 14px; }
        .card-content {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          align-items: start;
        }
        @media (max-width: 900px) {
          .card-content { grid-template-columns: 1fr; }
        }
        .left-col, .right-col {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .post-skeleton {
          display: flex;
          flex-direction: column;
          gap: 8px;
          padding: 16px;
          background: var(--tg-paper, #fafafd);
          border-radius: 10px;
        }
        .sk-line {
          height: 14px;
          background: linear-gradient(90deg, #eef0f3 0%, #f6f7f9 50%, #eef0f3 100%);
          background-size: 200% 100%;
          border-radius: 4px;
          animation: sk 1.2s linear infinite;
        }
        .sk-line.w70 { width: 70%; }
        .sk-line.w90 { width: 90%; }
        .sk-line.w60 { width: 60%; }
        @keyframes sk { to { background-position: -200% 0; } }
        .post-error {
          padding: 14px;
          background: rgba(229, 70, 70, 0.08);
          color: #c53030;
          border-radius: 10px;
          font-size: 13px;
        }
        .preview-eyebrow {
          font-size: 10.5px;
          font-weight: 800;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: var(--tg-mute, #5b6678);
        }
        .preview-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }
      `}</style>
    </div>
  )
}

function PostSummary({ post }: { post: ShowcasePost }) {
  return (
    <div className="ps">
      <div className="ps-status-row">
        <StatusBadge status={post.status} noindex={post.noindex} />
        {post.qualityScore !== null && (
          <span className="ps-quality" title="Quality score (L3)">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="12 2 15 9 22 9.3 17 14 19 21 12 17 5 21 7 14 2 9.3 9 9 12 2" />
            </svg>
            {post.qualityScore}
          </span>
        )}
      </div>

      <h1 className="ps-title">{post.title || 'Без заголовка'}</h1>
      {post.description && <p className="ps-desc">{post.description}</p>}

      <div className="ps-dates">
        {post.publishedAt && (
          <DateLine label="Опубликован" iso={post.publishedAt} />
        )}
        {post.deindexedAt && (
          <DateLine label="Скрыт из поиска" iso={post.deindexedAt} muted />
        )}
      </div>

      <div className="ps-kpi">
        <KpiCard
          label={pluralReaders(post.visitsTotal)}
          value={post.visitsTotal.toLocaleString('ru-RU')}
          hint="за всё время"
        />
        {post.url && (
          <a href={post.url} target="_blank" rel="noopener noreferrer" className="ps-link">
            Открыть страницу на портале →
          </a>
        )}
      </div>

      <style>{`
        .ps { display: flex; flex-direction: column; gap: 14px; }
        .ps-status-row { display: flex; align-items: center; gap: 8px; }
        .ps-quality {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 3px 8px;
          background: var(--tg-paper, #fafafd);
          color: var(--tg-ink-2, #333);
          border-radius: 6px;
          font-size: 11.5px;
          font-weight: 700;
        }
        .ps-quality svg { width: 11px; height: 11px; color: #e6a700; }
        .ps-title {
          margin: 0;
          font-size: 22px;
          font-weight: 800;
          line-height: 1.25;
          color: var(--tg-ink, #111);
        }
        .ps-desc {
          margin: 0;
          font-size: 14px;
          line-height: 1.5;
          color: var(--tg-ink-2, #333);
        }
        .ps-dates {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .ps-kpi {
          display: flex;
          flex-direction: column;
          gap: 10px;
          padding: 14px;
          background: var(--tg-paper, #fafafd);
          border-radius: 10px;
        }
        .ps-link {
          font-size: 13px;
          color: var(--tg-blue, #0066ff);
          font-weight: 600;
          text-decoration: none;
        }
        .ps-link:hover { text-decoration: underline; }
      `}</style>
    </div>
  )
}

function MetricsSection({
  metrics, loading, range, onRangeChange, postStatus,
}: {
  metrics: PostMetrics | null
  loading: boolean
  range: 30 | 90
  onRangeChange: (r: 30 | 90) => void
  postStatus: ShowcasePost['status']
}) {
  // Для непубликованных статусов метрик нет — секцию не показываем вообще.
  if (postStatus === 'queued' || postStatus === 'processing' || postStatus === 'failed' || postStatus === 'quality_rejected') {
    return null
  }

  if (loading) {
    return (
      <div className="metr-skel">
        <div className="metr-skel-bar" />
        <div className="metr-skel-chart" />
      </div>
    )
  }

  if (!metrics) {
    return (
      <div className="metr-empty">
        Метрики пока не подтянулись. Попробуйте обновить страницу через минуту — портал агрегирует данные с&nbsp;задержкой.
        <style>{`
          .metr-empty {
            padding: 14px 16px;
            background: var(--tg-paper, #fafafd);
            color: var(--tg-mute, #5b6678);
            border-radius: 10px;
            font-size: 13px;
            line-height: 1.5;
          }
        `}</style>
      </div>
    )
  }

  // B7 теперь возвращает единый ключ `visits` (B fix 17.06, commit faff6e98).
  // Оставляем минимальный defensive — если totals=undefined / days=null —
  // не падаем, рендерим empty-state.
  const days = Array.isArray(metrics.days) ? metrics.days : []
  const rawTotals = (metrics.totals ?? {}) as Partial<VisitsBucket>
  const totals: VisitsBucket = {
    visits: rawTotals.visits ?? 0,
    yandex: rawTotals.yandex ?? 0,
    google: rawTotals.google ?? 0,
    other: rawTotals.other ?? 0,
  }
  const maxVisits = Math.max(1, ...days.map((d) => d.visits ?? 0))
  const hasAny = totals.visits > 0

  return (
    <div className="metr">
      <div className="metr-head">
        <div>
          <div className="metr-eyebrow">Читателей за период</div>
          <div className="metr-value">{totals.visits.toLocaleString('ru-RU')}</div>
        </div>
        <RangeSwitch value={range} onChange={onRangeChange} />
      </div>

      {hasAny ? (
        <Chart days={days} maxVisits={maxVisits} />
      ) : (
        <div className="metr-chart-empty">
          За последние {range} дней страницу ещё никто не открывал. Пост в&nbsp;поиске — нужно время на&nbsp;индексацию.
        </div>
      )}

      {hasAny && <SourcesBreakdown totals={totals} />}

      <SearchMetricsBlock metrics={metrics.searchMetrics} />

      <style>{`
        .metr {
          display: flex;
          flex-direction: column;
          gap: 14px;
          padding: 16px;
          background: var(--tg-surface, #fff);
          border: 1px solid var(--tg-line-2, #f0f0f4);
          border-radius: 12px;
        }
        .metr-head {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          gap: 12px;
        }
        .metr-eyebrow {
          font-size: 10.5px;
          font-weight: 800;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: var(--tg-mute, #5b6678);
          margin-bottom: 4px;
        }
        .metr-value {
          font-size: 28px;
          font-weight: 800;
          color: var(--tg-ink, #111);
          line-height: 1;
        }
        .metr-chart-empty {
          padding: 28px 16px;
          background: var(--tg-paper, #fafafd);
          border-radius: 10px;
          font-size: 12.5px;
          color: var(--tg-mute, #5b6678);
          text-align: center;
          line-height: 1.5;
        }
        .metr-skel {
          display: flex;
          flex-direction: column;
          gap: 10px;
          padding: 16px;
          background: var(--tg-surface, #fff);
          border: 1px solid var(--tg-line-2, #f0f0f4);
          border-radius: 12px;
        }
        .metr-skel-bar {
          height: 28px;
          width: 50%;
          background: linear-gradient(90deg, #eef0f3 0%, #f6f7f9 50%, #eef0f3 100%);
          background-size: 200% 100%;
          border-radius: 4px;
          animation: sk 1.2s linear infinite;
        }
        .metr-skel-chart {
          height: 130px;
          background: linear-gradient(90deg, #eef0f3 0%, #f6f7f9 50%, #eef0f3 100%);
          background-size: 200% 100%;
          border-radius: 8px;
          animation: sk 1.2s linear infinite;
        }
      `}</style>
    </div>
  )
}

function RangeSwitch({ value, onChange }: { value: 30 | 90; onChange: (r: 30 | 90) => void }) {
  return (
    <div className="rng">
      <button className={value === 30 ? 'on' : ''} onClick={() => onChange(30)}>30д</button>
      <button className={value === 90 ? 'on' : ''} onClick={() => onChange(90)}>90д</button>
      <style>{`
        .rng {
          display: inline-flex;
          background: var(--tg-paper, #fafafd);
          border-radius: 8px;
          padding: 2px;
        }
        .rng button {
          padding: 5px 12px;
          font-family: inherit;
          font-size: 12px;
          font-weight: 700;
          background: transparent;
          border: 0;
          color: var(--tg-mute, #5b6678);
          border-radius: 6px;
          cursor: pointer;
        }
        .rng button.on {
          background: var(--tg-surface, #fff);
          color: var(--tg-ink, #111);
          box-shadow: 0 1px 2px rgba(0,0,0,0.06);
        }
      `}</style>
    </div>
  )
}

function Chart({ days, maxVisits }: { days: PostMetrics['days']; maxVisits: number }) {
  // Простой SVG-bar chart по дням — без зависимостей.
  const width = 100 // используем viewBox через проценты
  const height = 140
  const barGap = 2
  const barWidth = Math.max(1, (width - barGap * (days.length - 1)) / days.length)

  return (
    <div className="chart">
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" className="chart-svg">
        {days.map((d, i) => {
          const h = (d.visits / maxVisits) * (height - 20)
          const x = i * (barWidth + barGap)
          const y = height - h
          return (
            <g key={d.date}>
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={Math.max(1, h)}
                fill="var(--tg-blue, #0066ff)"
                opacity={d.visits > 0 ? 0.85 : 0.15}
              >
                <title>{`${formatDate(d.date)}: ${d.visits} ${pluralReaders(d.visits)}`}</title>
              </rect>
            </g>
          )
        })}
      </svg>
      <div className="chart-axis">
        <span>{formatShortDate(days[0]?.date)}</span>
        <span>{formatShortDate(days[Math.floor(days.length / 2)]?.date)}</span>
        <span>{formatShortDate(days[days.length - 1]?.date)}</span>
      </div>
      <style>{`
        .chart { display: flex; flex-direction: column; gap: 6px; }
        .chart-svg {
          width: 100%;
          height: 130px;
          display: block;
        }
        .chart-axis {
          display: flex;
          justify-content: space-between;
          font-size: 10.5px;
          color: var(--tg-mute, #5b6678);
          font-variant-numeric: tabular-nums;
        }
      `}</style>
    </div>
  )
}

function SourcesBreakdown({ totals }: { totals: PostMetrics['totals'] }) {
  const sum = totals.visits || 1
  const items = [
    { key: 'yandex', label: 'Яндекс', value: totals.yandex, color: '#ff5a5a' },
    { key: 'google', label: 'Google', value: totals.google, color: '#1a73e8' },
    { key: 'other', label: 'Прочее', value: totals.other, color: '#9ca3af' },
  ]
  return (
    <div className="src">
      <div className="src-head">Откуда приходят</div>
      <div className="src-bar">
        {items.map((it) => (
          <div
            key={it.key}
            className="src-seg"
            style={{ flexGrow: it.value, background: it.color }}
            title={`${it.label}: ${it.value} (${Math.round((it.value / sum) * 100)}%)`}
          />
        ))}
      </div>
      <div className="src-legend">
        {items.map((it) => (
          <div key={it.key} className="src-li">
            <span className="src-dot" style={{ background: it.color }} />
            <span className="src-l">{it.label}</span>
            <span className="src-v">{it.value.toLocaleString('ru-RU')}</span>
            <span className="src-pct">{Math.round((it.value / sum) * 100)}%</span>
          </div>
        ))}
      </div>
      <style>{`
        .src { display: flex; flex-direction: column; gap: 8px; }
        .src-head {
          font-size: 10.5px;
          font-weight: 800;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: var(--tg-mute, #5b6678);
        }
        .src-bar {
          display: flex;
          height: 8px;
          border-radius: 4px;
          overflow: hidden;
          background: var(--tg-paper, #fafafd);
          gap: 2px;
        }
        .src-seg { min-width: 0; }
        .src-legend {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .src-li {
          display: grid;
          grid-template-columns: 8px 1fr auto auto;
          align-items: center;
          gap: 8px;
          font-size: 12.5px;
        }
        .src-dot { width: 8px; height: 8px; border-radius: 50%; }
        .src-l { color: var(--tg-ink-2, #333); }
        .src-v { color: var(--tg-ink, #111); font-weight: 700; font-variant-numeric: tabular-nums; }
        .src-pct { color: var(--tg-mute, #5b6678); font-variant-numeric: tabular-nums; min-width: 36px; text-align: right; }
      `}</style>
    </div>
  )
}

function SearchMetricsBlock({ metrics }: { metrics: PostMetrics['searchMetrics'] }) {
  if (metrics.status === 'pending_oauth') {
    return (
      <div className="sm-pending">
        <div className="sm-pending-head">🔍 Данные Я.Вебмастера ещё не накопились</div>
        <p>
          Первые запросы обычно появляются через <b>7-14&nbsp;дней</b> после публикации —
          столько занимает попадание в&nbsp;индекс. Тогда здесь покажем показы в&nbsp;Я/Google,
          переходы, среднюю позицию и&nbsp;топ-запросы.
        </p>
        <style>{`
          .sm-pending {
            padding: 12px 14px;
            background: var(--tg-blue-bg, #ebf2ff);
            border-radius: 10px;
          }
          .sm-pending-head {
            font-size: 12px;
            font-weight: 800;
            color: var(--tg-blue-press, #0052cc);
            margin-bottom: 4px;
          }
          .sm-pending p {
            margin: 0;
            font-size: 12px;
            line-height: 1.5;
            color: var(--tg-blue-press, #0052cc);
          }
        `}</style>
      </div>
    )
  }

  // B17 + per-post-seo-metrics (01.07): per-post snapshot last30d.
  // avgPosition = null если impressions=0. topQueries отфильтрованы
  // `impressions >= 10` на C/B стороне; на клиенте режем повторно — двойная
  // защита от шума на случай если старый B/C не фильтровал.
  const relevantQueries = (metrics.topQueries ?? [])
    .filter((q) => q.impressions >= 10)
    .slice(0, 5)
  return (
    <div className="sm">
      <div className="sm-head">Как находят этот пост</div>
      <div className="sm-sub">Запросы, по которым люди приходят · и ваше место в поиске</div>
      <div className="sm-kpi">
        <SmKpi label="Показов" value={metrics.impressions.toLocaleString('ru-RU')} />
        <SmKpi label="Переходов" value={metrics.clicks.toLocaleString('ru-RU')} />
        <SmKpi
          label="Сред. позиция"
          value={metrics.avgPosition !== null ? Math.round(metrics.avgPosition).toString() : '—'}
        />
      </div>
      {relevantQueries.length > 0 ? (
        <div className="sm-queries">
          {relevantQueries.map((q, i) => (
            <div key={`${q.query}-${i}`} className="sm-q" title={q.query}>
              <span className="sm-q-text">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="7" />
                  <path d="m21 21-4.3-4.3" />
                </svg>
                <span>{q.query}</span>
              </span>
              <span className="sm-q-pos">{Math.round(q.position)}&nbsp;место</span>
              <span className="sm-q-impressions">{q.impressions.toLocaleString('ru-RU')}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="sm-empty">
          Данные Я.Вебмастера накапливаются — первые запросы появятся через 7-14 дней после публикации.
        </div>
      )}
      <div className="sm-foot">
        Источник: {formatSearchSource(metrics.source)} · обновлено {formatSnapshotShort(metrics.snapshotAt)}
      </div>
      <style>{`
        .sm { display: flex; flex-direction: column; gap: 8px; }
        .sm-head {
          font-size: 14px;
          font-weight: 700;
          color: var(--tg-ink, #111);
        }
        .sm-sub {
          font-size: 12px;
          color: var(--tg-mute, #5b6678);
          margin-top: -4px;
        }
        .sm-kpi {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 8px;
          margin-top: 4px;
        }
        .sm-queries { display: flex; flex-direction: column; gap: 2px; margin-top: 6px; }
        .sm-q {
          display: grid;
          grid-template-columns: 1fr auto auto;
          gap: 12px;
          align-items: center;
          font-size: 13px;
          padding: 8px 0;
          border-bottom: 1px solid var(--tg-line-2, #f0f0f4);
        }
        .sm-q:last-child { border-bottom: 0; }
        .sm-q-text {
          display: flex;
          align-items: center;
          gap: 8px;
          color: var(--tg-ink, #111);
          min-width: 0;
        }
        .sm-q-text svg { width: 14px; height: 14px; color: var(--tg-mute, #5b6678); flex-shrink: 0; }
        .sm-q-text span {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .sm-q-pos {
          font-size: 12px;
          font-weight: 700;
          color: var(--tg-blue-press, #0052cc);
          font-variant-numeric: tabular-nums;
          white-space: nowrap;
        }
        .sm-q-impressions {
          font-size: 13px;
          font-weight: 700;
          color: var(--tg-ink, #111);
          font-variant-numeric: tabular-nums;
          min-width: 40px;
          text-align: right;
        }
        .sm-empty {
          padding: 14px 16px;
          background: var(--tg-surface-2, #f5f5f9);
          border-radius: 10px;
          font-size: 12.5px;
          color: var(--tg-mute, #5b6678);
          line-height: 1.5;
          margin-top: 4px;
        }
        .sm-foot {
          font-size: 11px;
          color: var(--tg-mute, #5b6678);
          margin-top: 8px;
          font-variant-numeric: tabular-nums;
        }
      `}</style>
    </div>
  )
}

function formatSearchSource(source: 'yandex_webmaster' | 'gsc' | 'hybrid'): string {
  if (source === 'gsc') return 'Google Search Console'
  if (source === 'hybrid') return 'Я.Вебмастер + GSC'
  return 'Я.Вебмастер'
}

function formatSnapshotShort(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const diffH = Math.round((Date.now() - d.getTime()) / 3_600_000)
  if (diffH < 1) return 'только что'
  if (diffH < 24) return `${diffH}ч назад`
  const diffD = Math.round(diffH / 24)
  if (diffD === 1) return 'вчера'
  if (diffD < 7) return `${diffD} дн. назад`
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: 'short' })
}

function SmKpi({ label, value }: { label: string; value: string }) {
  return (
    <div className="smk">
      <div className="smk-v">{value}</div>
      <div className="smk-l">{label}</div>
      <style>{`
        .smk {
          padding: 8px 10px;
          background: var(--tg-paper, #fafafd);
          border-radius: 8px;
        }
        .smk-v { font-size: 16px; font-weight: 800; color: var(--tg-ink, #111); }
        .smk-l { font-size: 10.5px; color: var(--tg-mute, #5b6678); margin-top: 1px; }
      `}</style>
    </div>
  )
}

function formatShortDate(iso?: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })
}

function StatusBadge({
  status, noindex,
}: { status: ShowcasePost['status']; noindex: boolean }) {
  const map = {
    queued: { label: 'В очереди', cls: 'b-q' },
    processing: { label: 'Готовится', cls: 'b-q' },
    published: { label: noindex ? 'Опубликован (без индекса)' : 'В поиске', cls: noindex ? 'b-w' : 'b-ok' },
    quality_rejected: { label: 'Не прошёл проверку качества', cls: 'b-w' },
    deindexed: { label: 'Скрыт из поиска', cls: 'b-m' },
    failed: { label: 'Ошибка', cls: 'b-e' },
  } as const
  const e = map[status]
  return (
    <span className={`badge ${e.cls}`}>
      {e.label}
      <style>{`
        .badge {
          display: inline-block;
          padding: 3px 9px;
          border-radius: 6px;
          font-size: 11.5px;
          font-weight: 700;
        }
        .b-ok { background: #e8f6ec; color: #0d8a3f; }
        .b-q { background: #fff5d6; color: #8a6800; }
        .b-w { background: #fde8d6; color: #b94900; }
        .b-m { background: #ebeef3; color: #5b6678; }
        .b-e { background: #fde0e0; color: #c53030; }
      `}</style>
    </span>
  )
}

function DateLine({ label, iso, muted }: { label: string; iso: string; muted?: boolean }) {
  return (
    <span className={`dl ${muted ? 'muted' : ''}`}>
      <span className="dl-l">{label}:</span> <span className="dl-v">{formatDate(iso)}</span>
      <style>{`
        .dl { font-size: 12.5px; color: var(--tg-ink-2, #333); }
        .dl.muted { color: var(--tg-mute, #5b6678); }
        .dl-l { color: var(--tg-mute, #5b6678); }
        .dl-v { font-weight: 600; }
      `}</style>
    </span>
  )
}

function KpiCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="kpi">
      <div className="kpi-v">{value}</div>
      <div className="kpi-l">{label}{hint ? ` · ${hint}` : ''}</div>
      <style>{`
        .kpi { display: flex; flex-direction: column; gap: 2px; }
        .kpi-v { font-size: 28px; font-weight: 800; color: var(--tg-ink, #111); line-height: 1; }
        .kpi-l { font-size: 12px; color: var(--tg-mute, #5b6678); }
      `}</style>
    </div>
  )
}

/**
 * Простая live-версия browser-frame для published-постов — iframe со `src`
 * на реальную страницу портала. Без srcDoc/inject'ов: страница public, CSS/JS
 * грузятся как в обычной вкладке.
 */
function BrowserFrameLive({ url }: { url: string }) {
  return (
    <div className="browser">
      <div className="browser-bar">
        <span className="dot dot-r" />
        <span className="dot dot-y" />
        <span className="dot dot-g" />
        <div className="browser-url">{url}</div>
      </div>
      <div className="browser-body">
        <iframe
          title="Страница на портале"
          src={url}
          className="bf-iframe"
          sandbox="allow-same-origin allow-popups allow-scripts"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </div>
      <style>{`
        .browser {
          background: white;
          border: 1px solid var(--tg-line-2, #f0f0f4);
          border-radius: 14px;
          overflow: hidden;
          box-shadow: 0 8px 24px -8px rgba(0,0,0,0.06);
        }
        .browser-bar {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 14px;
          background: var(--tg-paper, #fafafd);
          border-bottom: 1px solid var(--tg-line-2, #f0f0f4);
        }
        .dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
        .dot-r { background: #ff6b6b; }
        .dot-y { background: #ffd93d; }
        .dot-g { background: #6bcf7f; }
        .browser-url {
          flex: 1;
          font-family: ui-monospace, SFMono-Regular, monospace;
          font-size: 11.5px;
          color: var(--tg-mute, #5b6678);
          padding: 4px 10px;
          background: white;
          border-radius: 6px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .browser-body {
          height: 520px;
          overflow: hidden;
          display: flex;
        }
        .bf-iframe { width: 100%; height: 100%; border: 0; }
      `}</style>
    </div>
  )
}

function BrowserFrame({
  loading, error, preview, url,
}: {
  loading: boolean
  error: string | null
  preview: PreviewResponse | null
  url: string
}) {
  return (
    <div className="browser">
      <div className="browser-bar">
        <span className="dot dot-r" />
        <span className="dot dot-y" />
        <span className="dot dot-g" />
        <div className="browser-url">{url}</div>
      </div>

      <div className="browser-body">
        {loading ? (
          <div className="bf-loading">
            <div className="bf-spinner" />
            <span>Загружаем превью…</span>
          </div>
        ) : error ? (
          <PreviewError code={error} />
        ) : preview ? (
          <iframe
            title="Превью страницы"
            sandbox="allow-same-origin"
            srcDoc={injectBaseUrl(preview.html)}
            className="bf-iframe"
          />
        ) : null}
      </div>

      <style>{`
        .browser {
          background: white;
          border: 1px solid var(--tg-line-2, #f0f0f4);
          border-radius: 14px;
          overflow: hidden;
          box-shadow: 0 8px 24px -8px rgba(0,0,0,0.06);
        }
        .browser-bar {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 14px;
          background: var(--tg-paper, #fafafd);
          border-bottom: 1px solid var(--tg-line-2, #f0f0f4);
        }
        .dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
        .dot-r { background: #ff6b6b; }
        .dot-y { background: #ffd93d; }
        .dot-g { background: #6bcf7f; }
        .browser-url {
          flex: 1;
          font-family: ui-monospace, SFMono-Regular, monospace;
          font-size: 11.5px;
          color: var(--tg-mute, #5b6678);
          padding: 4px 10px;
          background: white;
          border-radius: 6px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .browser-body {
          height: 520px;
          overflow: hidden;
          display: flex;
        }
        .bf-iframe {
          width: 100%;
          height: 100%;
          border: 0;
        }
        .bf-loading {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 10px;
          color: var(--tg-mute, #5b6678);
          font-size: 13px;
        }
        .bf-spinner {
          width: 28px;
          height: 28px;
          border: 3px solid var(--tg-line-2, #f0f0f4);
          border-top-color: var(--tg-blue, #0066ff);
          border-radius: 50%;
          animation: bf-spin 0.7s linear infinite;
        }
        @keyframes bf-spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  )
}

function PreviewError({ code }: { code: string }) {
  // B18 коды (контракт B 15.06):
  // - POST_DEINDEXED (410) — пост убран из поиска в гонке между B6 list и B18.
  //   Обычно фронт скипает через skipPreview, но если race-condition попал сюда.
  // - NOT_PUBLISHED (404) — рендер ещё не завершён, попробовать через минуту.
  // - POST_NOT_FOUND (404) — пост вообще не существует на портале.
  const variant = (() => {
    if (code === 'POST_DEINDEXED') return {
      icon: '🙈',
      title: 'Страница убрана из поиска',
      text: 'Пост был деиндексирован — превью больше не доступно.',
      tone: 'mute' as const,
    }
    if (code === 'NOT_PUBLISHED') return {
      icon: '⏳',
      title: 'Превью готовится',
      text: 'Страница ещё не отрендерилась на портале. Загляните через минуту-другую.',
      tone: 'info' as const,
    }
    if (code === 'POST_NOT_FOUND') return {
      icon: '❓',
      title: 'Пост не найден',
      text: 'На портале нет страницы для этого поста. Возможно она была удалена.',
      tone: 'warn' as const,
    }
    return {
      icon: '⚠️',
      title: 'Не удалось загрузить превью',
      text: 'Попробуйте обновить страницу. Если повторится — мы уже видим ошибку в логах.',
      tone: 'warn' as const,
    }
  })()

  return (
    <div className={`bf-error bf-error-${variant.tone}`}>
      <div className="bf-error-icon">{variant.icon}</div>
      <h4 className="bf-error-title">{variant.title}</h4>
      <p>{variant.text}</p>

      <style>{`
        .bf-error {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 32px;
          text-align: center;
          color: var(--tg-mute, #5b6678);
          font-size: 13px;
        }
        .bf-error-icon { font-size: 28px; }
        .bf-error-title {
          margin: 0;
          font-size: 14px;
          font-weight: 700;
          color: var(--tg-ink, #111);
        }
        .bf-error p { margin: 0; max-width: 360px; line-height: 1.5; }
        .bf-error-info .bf-error-icon { color: var(--tg-blue, #0066ff); }
        .bf-error-warn .bf-error-icon { color: #c53030; }
        .bf-error-mute .bf-error-title { color: var(--tg-mute, #5b6678); }
      `}</style>
    </div>
  )
}

function NoPreviewNotice({ status }: { status: ShowcasePost['status'] }) {
  const map = {
    deindexed: {
      icon: '🙈',
      title: 'Пост скрыт из поиска',
      text: 'Страница убрана с портала, поэтому превью больше не доступно. Поисковики удалят её из выдачи за 5-14 дней.',
    },
    quality_rejected: {
      icon: '⚠️',
      title: 'Пост не прошёл проверку качества',
      text: 'AI-фильтр посчитал пост слишком коротким или неинформативным для поиска — страница не публиковалась.',
    },
    failed: {
      icon: '⚠️',
      title: 'Не удалось опубликовать пост',
      text: 'Произошла техническая ошибка при создании страницы — превью недоступно. Попробуем переопубликовать автоматически.',
    },
  } as const
  const e = (status in map) ? map[status as keyof typeof map] : null
  if (!e) return null
  return (
    <div className="np">
      <div className="np-icon">{e.icon}</div>
      <h4 className="np-title">{e.title}</h4>
      <p className="np-text">{e.text}</p>
      <style>{`
        .np {
          padding: 40px 28px;
          background: var(--tg-paper, #fafafd);
          border: 1px dashed var(--tg-line-2, #f0f0f4);
          border-radius: 14px;
          text-align: center;
        }
        .np-icon { font-size: 32px; margin-bottom: 8px; }
        .np-title {
          margin: 0 0 6px;
          font-size: 15px;
          font-weight: 700;
          color: var(--tg-ink, #111);
        }
        .np-text {
          margin: 0 auto;
          max-width: 340px;
          font-size: 13px;
          color: var(--tg-mute, #5b6678);
          line-height: 1.5;
        }
      `}</style>
    </div>
  )
}

function MetaItem({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <span className={`meta-pill ${warn ? 'warn' : ''}`}>
      <span className="m-l">{label}</span>
      <span className="m-v">{value}</span>
      <style>{`
        .meta-pill {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 3px 8px;
          background: var(--tg-paper, #fafafd);
          border-radius: 6px;
          font-size: 11.5px;
        }
        .meta-pill.warn { background: rgba(229, 70, 70, 0.12); }
        .m-l { color: var(--tg-mute, #5b6678); }
        .m-v { color: var(--tg-ink, #111); font-weight: 700; }
        .meta-pill.warn .m-v { color: #c53030; }
      `}</style>
    </span>
  )
}

/**
 * Inject `<base href="https://telegraphyx.ru/">` в head preview-HTML.
 *
 * Без base все относительные пути (/static/css, /js/app.js, /images/logo.svg)
 * резолвятся к about:srcdoc/* → 404 → стили/скрипты не грузятся → iframe
 * выглядит «голым» (см. 17.06.2026 у Виктора: дублированная навигация,
 * нет картинок, текст не отформатирован).
 *
 * Долгосрочный фикс — попросить B добавлять <base> на стороне preview-html
 * endpoint'а. Пока inject на фронте.
 */
function injectBaseUrl(html: string): string {
  if (/<base\s/i.test(html)) return html
  const baseTag = '<base href="https://telegraphyx.ru/">'
  if (/<head[^>]*>/i.test(html)) {
    return html.replace(/(<head[^>]*>)/i, `$1${baseTag}`)
  }
  // Fallback — если <head> вообще нет, вставляем перед <html> или в начало
  if (/<html[^>]*>/i.test(html)) {
    return html.replace(/(<html[^>]*>)/i, `$1<head>${baseTag}</head>`)
  }
  return `${baseTag}${html}`
}

function previewUrlFromHtml(html?: string): string | null {
  if (!html) return null
  const m = html.match(/<link[^>]*rel=["']canonical["'][^>]*href=["']([^"']+)["']/i)
  if (m) return m[1]
  return null
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: 'short', year: 'numeric' })
}

function pluralReaders(n: number): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return 'читатель'
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'читателя'
  return 'читателей'
}

function statusLabel(s: string): string {
  switch (s) {
    case 'published': return 'опубликован'
    case 'deindexed': return 'скрыт'
    case 'quality_rejected': return 'не прошёл'
    case 'pending': return 'готовится'
    case 'failed': return 'ошибка'
    default: return s
  }
}
